/* ChipMultiSelect — a fully-visual multi-select rendered as toggle chips.
 * Each option is a real toggle (aps-filter-chip): click to grant, click to
 * revoke. Active chips carry a check; the set IS the value (no append/remove
 * UI, no free text). Used by the identity forms for user Roles and role
 * Journeys. */
import React from 'react';
import {
    Check
} from 'lucide-react';

export type ChipOption = {
    value: string;
    label: string;
};

type Props = {
    options: ChipOption[];
    value: string[];
    onChange: (next: string[]) => void;
    /** Shown when there ARE options but none is selected. */
    placeholder?: string;
    /** Shown when there are NO options at all. */
    emptyHint?: string;
    disabled?: boolean;
};

export default function ChipMultiSelect({
    options,
    value,
    onChange,
    placeholder = 'None selected',
    emptyHint = 'No options available.',
    disabled = false
}: Props) {
    const selected = new Set(value);

    const toggle = (v: string) => {
        if (disabled) return;
        const next = new Set(selected);
        if (next.has(v)) next.delete(v);
        else next.add(v);
        // Preserve option order for a stable, readable set.
        onChange(options.filter((o) => next.has(o.value)).map((o) => o.value));
    };

    if (options.length === 0) {
        return <p className="aps-help" style={{
 marginTop: 0
        }}>{emptyHint}</p>;
    }

    return (
        <div>
            <div className="aps-chip-row" style={{
 gap: 6
}}>
                {options.map((o) => {
                    const on = selected.has(o.value);
                    return (
                        <button key={o.value} type="button"
                            className={'aps-filter-chip' + (on ? ' active' : '')}
                            disabled={disabled}
                            aria-pressed={on}
                            onClick={() => toggle(o.value)}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 5
                            }}>
                            {on && <Check size={12} />}
                            {o.label}
                        </button>
                    );
                })}
            </div>
            {selected.size === 0 && (
                <p className="aps-help" style={{
 marginTop: 6
}}>{placeholder}</p>
            )}
        </div>
    );
}
